import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
  CircularProgress, Alert, Snackbar, Chip
} from '@mui/material';
import { stripGlSystemLabel, toChartOfAccountGlSystem } from '../../constants/glSyncDocumentTypes';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateCardSx,
  harmonateProceedButtonSx,
} from '../../theme/harmonateStyles';

const GLSyncMappingStep = ({
  sourceGL: propSourceGL,
  destinationGL: propDestinationGL,
  selectedFile: propSelectedFile,
  onBackToStepper: propOnBackToStepper,
  fundId: propFundId = null,
  fundCode: propFundCode = null,
  fundName: propFundName = null,
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mappingData, setMappingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('error');
  const [resolvedFundCode, setResolvedFundCode] = useState(
    propFundCode ? String(propFundCode).trim() : null
  );
  const [resolvedFundName, setResolvedFundName] = useState(
    propFundName ? String(propFundName).trim() : null
  );
  const [fundResolveDone, setFundResolveDone] = useState(false);

  // Prefer props from Trial Balance Compare; fall back to navigation state
  const {
    uploadData,
    selectedFile: stateSelectedFile,
    onBackToStepper: stateOnBackToStepper,
    sourceGL: stateSourceGL,
    destinationGL: stateDestinationGL,
    fundId: stateFundId = null,
    fundCode: stateFundCode = null,
    fundName: stateFundName = null,
  } = location.state || {};

  const selectedFile = propSelectedFile || stateSelectedFile;
  const onBackToStepper = propOnBackToStepper || stateOnBackToStepper;
  const sourceGL = propSourceGL || stateSourceGL;
  const destinationGL = propDestinationGL || stateDestinationGL;

  const cardFundId = (() => {
    const raw =
      propFundId ||
      stateFundId ||
      uploadData?.fundId ||
      selectedFile?.fundId ||
      selectedFile?.uploadData?.fundId ||
      null;
    return raw ? String(raw) : null;
  })();

  const incomingFundCode = (() => {
    const raw =
      propFundCode ||
      stateFundCode ||
      uploadData?.fundCode ||
      selectedFile?.fundCode ||
      selectedFile?.uploadData?.fundCode ||
      null;
    return raw ? String(raw).trim() : null;
  })();

  const incomingFundName = (() => {
    const raw =
      propFundName ||
      stateFundName ||
      uploadData?.fundName ||
      selectedFile?.fundName ||
      selectedFile?.uploadData?.fundName ||
      null;
    return raw ? String(raw).trim() : null;
  })();

  const cardFundCode = incomingFundCode || resolvedFundCode || null;
  const cardFundName = incomingFundName || resolvedFundName || null;
  const fundLabel = cardFundCode || cardFundName || null;

  const sourceSystem =
    stripGlSystemLabel(sourceGL) ||
    stripGlSystemLabel(uploadData?.source) ||
    stripGlSystemLabel(selectedFile?.source) ||
    'EFRONT';
  const destinationSystem =
    stripGlSystemLabel(destinationGL) ||
    stripGlSystemLabel(uploadData?.destination) ||
    stripGlSystemLabel(selectedFile?.destination) ||
    'ALLVUE';

  /** Build fund_code keys used in Postgres/Snowflake (LVI, LVI_GL, etc.). */
  const fundCodeCandidates = (code) => {
    const c = String(code || '').trim();
    if (!c) return [];
    const out = new Set([c]);
    if (c.toUpperCase().endsWith('_GL') && c.length > 3) {
      out.add(c.slice(0, -3).trim());
    } else {
      out.add(`${c}_GL`);
    }
    return [...out].filter(Boolean);
  };

  // Resolve fund code from fundId / fundName when TB row only has name (chip showed name, not code)
  useEffect(() => {
    let cancelled = false;
    const resolveFund = async () => {
      if (incomingFundCode) {
        setResolvedFundCode(incomingFundCode);
        setResolvedFundName(incomingFundName);
        setFundResolveDone(true);
        return;
      }
      if (!cardFundId && !incomingFundName) {
        setResolvedFundCode(null);
        setResolvedFundName(null);
        setFundResolveDone(true);
        return;
      }
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id')?.trim();
      if (!token || !tenantId) {
        setFundResolveDone(true);
        return;
      }
      try {
        const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const url = `${base}/conductor/funds/by-tenant-entity?tenantId=${encodeURIComponent(tenantId)}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        });
        if (!res.ok) {
          if (!cancelled) setFundResolveDone(true);
          return;
        }
        const list = await res.json().catch(() => []);
        const funds = Array.isArray(list) ? list : [];
        let match = null;
        if (cardFundId) {
          match = funds.find((f) => String(f.id || f.fundId || '') === String(cardFundId));
        }
        if (!match && incomingFundName) {
          const nameLc = incomingFundName.toLowerCase();
          match = funds.find(
            (f) => String(f.fundName || '').trim().toLowerCase() === nameLc
          );
        }
        if (!cancelled && match) {
          setResolvedFundCode(match.fundCode ? String(match.fundCode).trim() : null);
          setResolvedFundName(
            match.fundName ? String(match.fundName).trim() : incomingFundName
          );
        } else if (!cancelled) {
          setResolvedFundName(incomingFundName);
        }
      } catch (e) {
        console.warn('Failed to resolve fund for TB mapping step:', e);
      } finally {
        if (!cancelled) setFundResolveDone(true);
      }
    };
    setFundResolveDone(false);
    void resolveFund();
    return () => {
      cancelled = true;
    };
  }, [cardFundId, incomingFundCode, incomingFundName]);

  const showToast = (message, severity = 'error') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  const fetchSystemAccounts = async (systemName, { fundFilter = false, fundId = null } = {}) => {
    const token = localStorage.getItem('token');
    const storedTenantId = localStorage.getItem('tenant_id')?.trim();
    if (!token || !storedTenantId || !systemName) return [];

    const primaryGlSystem = toChartOfAccountGlSystem(systemName);
    const legacyGlSystem =
      primaryGlSystem.endsWith('_GL') && primaryGlSystem.length > 3
        ? primaryGlSystem.slice(0, -3)
        : '';

    const fetchOnce = async (glSystem) => {
      const url = new URL(
        `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/')}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property`
      );
      url.searchParams.set('tenant_id', storedTenantId);
      url.searchParams.set('gl_system', glSystem);
      url.searchParams.set('system_type', 'GL_SYNC');
      if (fundFilter) {
        url.searchParams.set('fund_filter', 'true');
        if (fundId) url.searchParams.set('fund_id', fundId);
      }
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch GL accounts (${response.status})`);
      }
      const raw = await response.json();
      const list = Array.isArray(raw) ? raw : [];
      return list.map((row) => ({
        ...row,
        glId: row.glId ?? row.ledgerAccountId ?? null,
      }));
    };

    let rows = await fetchOnce(primaryGlSystem);
    if (rows.length === 0 && legacyGlSystem && legacyGlSystem !== primaryGlSystem) {
      rows = await fetchOnce(legacyGlSystem);
    }
    return rows;
  };

  /** Postgres fund-scoped mappings (source of truth for multi-fund). */
  const fetchConductorGlAccountMappings = async (
    fromSystem,
    toSystem,
    { fundCode = null, fundId = null, fundName = null } = {}
  ) => {
    const token = localStorage.getItem('token');
    const tenant_id = localStorage.getItem('tenant_id')?.trim();
    if (!token || !tenant_id || !fromSystem || !toSystem) return [];

    try {
      const from_gl_source = toChartOfAccountGlSystem(fromSystem);
      const to_gl_source = toChartOfAccountGlSystem(toSystem);
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
      const candidates = fundCodeCandidates(fundCode);
      const codeSet = new Set(candidates.map((c) => c.toLowerCase()));

      const fetchPair = async (withFundFilter, code) => {
        const listUrl = new URL(`${base}conductor/gl-account-mappings/by-filter`);
        listUrl.searchParams.set('tenant_id', tenant_id);
        listUrl.searchParams.set('from_gl_source', from_gl_source);
        listUrl.searchParams.set('to_gl_source', to_gl_source);
        if (withFundFilter) {
          listUrl.searchParams.set('fund_filter', 'true');
          if (code) listUrl.searchParams.set('fund_code', code);
        }
        const getRes = await fetch(listUrl.toString(), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!getRes.ok) return null;
        const list = await getRes.json();
        return Array.isArray(list) ? list : [];
      };

      // Try each fund_code variant with fund_filter (LVI then LVI_GL, etc.)
      for (const code of candidates) {
        const rows = await fetchPair(true, code);
        if (rows && rows.length > 0) return rows;
      }

      // Fetch all for this GL pair and filter client-side by fund id/code/name
      const all = (await fetchPair(false, null)) || [];
      if (!fundCode && !fundId && !fundName) {
        return all.filter((row) => {
          const rc = String(row.fundCode ?? row.fund_code ?? '').trim();
          return !rc;
        });
      }

      const nameLc = fundName ? String(fundName).trim().toLowerCase() : null;
      const idStr = fundId ? String(fundId) : null;
      return all.filter((row) => {
        const rc = String(row.fundCode ?? row.fund_code ?? '').trim();
        const rn = String(row.fundName ?? row.fund_name ?? '').trim().toLowerCase();
        const rid = String(row.fundId ?? row.fund_id ?? '').trim();
        if (idStr && rid && rid === idStr) return true;
        if (rc && codeSet.has(rc.toLowerCase())) return true;
        if (nameLc && rn && rn === nameLc) return true;
        // Also accept PROPERTY_ID-style codes that match candidates
        if (rc && fundCodeCandidates(rc).some((c) => codeSet.has(c.toLowerCase()))) {
          return true;
        }
        return false;
      });
    } catch (error) {
      console.error('Error fetching conductor GL account mappings:', error);
      return [];
    }
  };

  // Initialize data — fund-scoped like GL Mapping Edit
  useEffect(() => {
    // Wait until fund code is resolved when TB only has fund name/id
    if (!fundResolveDone) {
      setLoading(true);
      return;
    }

    const initializeData = async () => {
      setLoading(true);
      try {
        const fundCodeForFilter = cardFundCode || null;
        const fundIdForFilter = cardFundId;
        const fundNameForFilter = cardFundName || null;
        const label = fundCodeForFilter || fundNameForFilter || null;

        const [sourceAccounts, targetAccounts] = await Promise.all([
          fetchSystemAccounts(sourceSystem, {
            fundFilter: true,
            fundId: fundIdForFilter,
          }),
          fetchSystemAccounts(destinationSystem, { fundFilter: false }),
        ]);

        if (!sourceAccounts || sourceAccounts.length === 0) {
          throw new Error(
            'No GL accounts found for the specified systems' +
              (label ? ` and fund (${label})` : '')
          );
        }

        // Fund-scoped: Conductor mappings only (Snowflake link table is not fund-aware)
        const conductorMappings = await fetchConductorGlAccountMappings(
          sourceSystem,
          destinationSystem,
          {
            fundCode: fundCodeForFilter,
            fundId: fundIdForFilter,
            fundName: fundNameForFilter,
          }
        );

        const conductorByFromCode = new Map();
        (conductorMappings || []).forEach((row) => {
          const fc = String(row.fromGlCode ?? '').trim();
          if (fc && !conductorByFromCode.has(fc)) {
            conductorByFromCode.set(fc, row);
          }
        });

        const initialMappingData = sourceAccounts.map((account, index) => {
          const code = String(account.glCode ?? '').trim();
          const conductorRow = conductorByFromCode.get(code);
          let destinationAccount = null;
          if (conductorRow) {
            const toCode = String(conductorRow.toGlCode ?? '').trim();
            destinationAccount =
              targetAccounts.find((t) => String(t.glCode ?? '').trim() === toCode) || null;
          }

          let destinationField = '';
          if (destinationAccount) {
            destinationField = `${destinationAccount.glCode} - ${destinationAccount.glDescription}`;
          } else if (conductorRow) {
            destinationField = String(
              conductorRow.toGlAccount ||
                conductorRow.toCombined ||
                (conductorRow.toGlCode && conductorRow.toGlDescription
                  ? `${conductorRow.toGlCode} - ${conductorRow.toGlDescription}`
                  : conductorRow.toGlCode) ||
                ''
            ).trim();
          }

          return {
            id: account.glId,
            sourceField: account.glAccount,
            sourceGlSystem: account.glSystem,
            destinationField,
            destinationGlSystem: destinationAccount
              ? destinationAccount.glSystem
              : toChartOfAccountGlSystem(destinationSystem),
            mappingId: null,
            highlighted: index % 3 === 1,
          };
        });

        setMappingData(initialMappingData);
        setError(null);
      } catch (err) {
        console.error('Error initializing mapping step:', err);
        setError(err.message || 'Failed to load mapping data');
        setMappingData([]);
      } finally {
        setLoading(false);
      }
    };

    void initializeData();
  }, [
    fundResolveDone,
    sourceSystem,
    destinationSystem,
    cardFundId,
    cardFundCode,
    cardFundName,
  ]);

  const mappedCount = useMemo(
    () => mappingData.filter((r) => r.destinationField && String(r.destinationField).trim() !== '').length,
    [mappingData]
  );

  const handleBackToStepper = () => {
    if (onBackToStepper) {
      onBackToStepper();
    }
  };

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          bgcolor: ht.white,
          height: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TableContainer sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              width: '100%',
              height: '100%',
              minHeight: 240,
              py: 4,
            }}
          >
            <CircularProgress size={32} sx={{ color: ht.blue }} />
            <Typography variant="body1" color="text.secondary">
              Loading mapping data{fundLabel ? ` for ${fundLabel}` : ''}...
            </Typography>
          </Box>
        </TableContainer>
      </Paper>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => window.location.reload()} sx={{ ...harmonateProceedButtonSx, mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 0,
        height: '100%',
      }}
    >
      {/* Mapping Table — fixed height in stepper, body scrolls (same pattern as GL Mapping Edit) */}
      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          bgcolor: ht.white,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${ht.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
            Mapping
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`Source: ${sourceSystem}`}
              size="small"
              sx={{ bgcolor: ht.listBg, color: ht.text, fontWeight: 500 }}
            />
            <Chip
              label={`Destination: ${destinationSystem}`}
              size="small"
              sx={{ bgcolor: ht.listBg, color: ht.text, fontWeight: 500 }}
            />
            <Chip
              label={fundLabel ? `Fund: ${fundLabel}` : 'Fund: Unscoped'}
              size="small"
              sx={{ bgcolor: ht.listBg, color: ht.text, fontWeight: 500 }}
            />
          </Box>
        </Box>
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: ht.listBg }}>
                <TableCell sx={{ fontWeight: 600, color: ht.text, backgroundColor: ht.listBg, borderColor: ht.line }}>
                  Source GL Account ({sourceSystem})
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: ht.text, textAlign: 'center', width: '10%', backgroundColor: ht.listBg, borderColor: ht.line }}>
                  →
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: ht.text, backgroundColor: ht.listBg, borderColor: ht.line }}>
                  Destination GL Account ({destinationSystem})
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappingData.length > 0 ? (
                mappingData.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      backgroundColor: ht.white,
                      '&:hover': {
                        backgroundColor: ht.secondaryTab,
                      },
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}
                  >
                    <TableCell sx={{ width: '40%', color: ht.text, py: 0.5, borderColor: ht.line }}>
                      {row.sourceField}
                    </TableCell>
                    <TableCell sx={{ width: '10%', textAlign: 'center', py: 0.5, borderColor: ht.line }}>
                      <Typography variant="h6" sx={{ color: ht.green }}>
                        →
                      </Typography>
                    </TableCell>
                    <TableCell sx={{
                      width: '50%',
                      color: ht.text,
                      py: 0.5,
                      borderColor: ht.line,
                    }}>
                      {row.destinationField}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No GL accounts found for {sourceSystem} system
                      {fundLabel ? ` / fund ${fundLabel}` : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          component="footer"
          sx={{
            flexShrink: 0,
            borderTop: `1px solid ${ht.line}`,
            px: 2,
            py: 1,
            bgcolor: ht.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Typography variant="body2" color="text.secondary" component="span">
            {mappingData.length === 1 ? '1 record' : `${mappingData.length} records`}
            {' · '}
            {mappedCount === 1 ? '1 mapped' : `${mappedCount} mapped`}
          </Typography>
        </Box>
      </Paper>

      {/* Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleToastClose} severity={toastSeverity} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GLSyncMappingStep;
